import { ViewController } from '@estee/elc-service';
import { action, observable, computed } from 'mobx';
import { StoresMap } from '~domain/entities/StoresMap';
import { StoresMapPannedUseCase } from '~use-cases/StoresMapPannedUseCase';
import { StoresSearchBoxChangedUseCase } from '~use-cases/StoresSearchBoxChangedUseCase';
import { StoresMapMarkerClickedUseCase } from '~use-cases/StoresMapMarkerClickedUseCase';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { IStoresMapBounds } from '~interfaces/IStoresInterfaces';
import { Config } from '~domain/entities/Config';
import { StoresRepository } from '~repositories/StoresRepository';
import { GoogleMap } from '../../utils/MapsDynamicLoader';
import { StorePhoneClickedUseCase } from '~use-cases/StorePhoneClickedUseCase';
import { StoreInfoClickedUseCase } from '~use-cases/StoreInfoClickedUseCase';
import { events, emit } from '@estee/elc-service-bus';
import { StoreDetailsViewController } from './StoreDetailsViewController';

export interface IMarker {
    id: string;
    position: {
        latitude: number;
        longitude: number;
    };
    isSelected?: boolean;
    label?: string;
}

export class StoresMapViewController extends ViewController {
    @observable public storesMap: StoresMap;
    @observable public config: Config;
    @observable public storesRepository: StoresRepository;
    @observable public infoWindowOpen: boolean = false;
    @observable private storeDetailsViewController: StoreDetailsViewController;

    constructor() {
        super();
        this.storesMap = diContainer.get(serviceNames.storesMap);
        this.storesRepository = diContainer.get(serviceNames.storesRepository);
        this.config = diContainer.get(serviceNames.config);
        this.storeDetailsViewController = diContainer.get(serviceNames.storeDetailsViewController);
    }

    public setMap = (map: GoogleMap) => {
        this.storesMap.setMap(map);
    };

    public toggleInfoWindow = (open: boolean) => (this.infoWindowOpen = open);

    public resetSelectedStore = () => this.storesRepository.resetSelectedStore();

    @computed
    public get centerLocation() {
        return this.storesMap && this.storesMap.map && this.storesMap.map.getCenterLocation();
    }

    @computed
    get stores() {
        return this.storesRepository.stores;
    }

    @computed
    get currentStore() {
        const selectedMarkerId = this.storesRepository.selectedStoreId;

        return this.stores.find((store) => store.id === selectedMarkerId);
    }

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public boundsChanged = async (mapRef: any) => {
        const newBounds: IStoresMapBounds = {
            ne: {
                lat: mapRef.getBounds().getNorthEast().lat(),
                lng: mapRef.getBounds().getNorthEast().lng()
            },
            sw: {
                lat: mapRef.getBounds().getSouthWest().lat(),
                lng: mapRef.getBounds().getSouthWest().lng()
            }
        };

        const useCase = new StoresMapPannedUseCase(this.storesMap);
        await useCase.execute(newBounds);
    };

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public placesChanged = async (mapRef: any, inputRef: any) => {
        mapRef.panTo(inputRef.getPlaces()[0].geometry.stores);

        const useCase = new StoresSearchBoxChangedUseCase();
        await useCase.execute();
    };

    @action
    public onMarkerClicked = async (marker: IMarker) => {
        this.toggleInfoWindow(true);
        const { onExpandedStoreHoursIdChange } = this.storeDetailsViewController;
        onExpandedStoreHoursIdChange();
        const useCase = new StoresMapMarkerClickedUseCase();
        emit(events.STORE_MAP_MARKER_CLICKED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_map_selection',
            eventAction: `${marker?.label}`,
            eventLabel: 'displayed'
        });
        await useCase.execute(marker);
    };

    @action
    public onPhoneClick = () => {
        const currentStore = this.currentStore;
        if (currentStore) {
            const { id, name } = currentStore;
            new StorePhoneClickedUseCase(id, name).execute();
        }
    };

    @action
    public onStoreInfoLinkClick = () => {
        const currentStore = this.currentStore;
        if (currentStore) {
            const { id, name } = currentStore;
            emit(events.STORE_DETAILS_ADDRESS_LINK_CLICKED, {
                eventName: 'store_finder',
                eventCategory: 'store_finder_address_link_click',
                eventAction: `${name}`,
                eventLabel: `${name}`
            });
            new StoreInfoClickedUseCase(id, name).execute();
        }
    };

    @computed
    private get markers(): IMarker[] {
        return this.stores.map((item) => {
            return {
                id: item.id,
                position: {
                    latitude: item.geographical.coordinates.lat,
                    longitude: item.geographical.coordinates.lon
                },
                label: item.name,
                isSelected: item.id === this.storesRepository.selectedStoreId
            };
        });
    }

    @computed
    public get data() {
        return {
            config: this.config.config,
            storesMap: this.storesMap,
            store: this.currentStore,
            markers: this.markers,
            infoWindowOpen: this.infoWindowOpen
        };
    }
}
